import { useEffect, useRef } from "react";
import style from "./style.less";

const copy = {
  en: {
    subtitle: "How would you like to play?",
    match: "Match", matchNote: "Choose a room before starting",
    create: "Create room", createNote: "Choose rules, then confirm your room",
    unavailable: "This game is currently unavailable.",
    close: "Close",
  },
  id: {
    subtitle: "Pilih cara bermain",
    match: "Cari pemain", matchNote: "Pilih kamar sebelum mulai bermain",
    create: "Buat kamar", createNote: "Atur aturan, lalu konfirmasi kamar",
    unavailable: "Permainan ini sedang tidak tersedia.",
    close: "Tutup",
  },
  zh: {
    subtitle: "请选择进入方式",
    match: "匹配入口", matchNote: "选择场次，准备好后再开始",
    create: "开房间入口", createNote: "设置房间规则，再确认创建",
    unavailable: "该游戏暂不可用。",
    close: "关闭",
  },
};

export default function Mjh5GameEntry({ game, language, onSelect, onClose }) {
  const panel = useRef(null);
  const firstAction = useRef(null);
  const selected = useRef(false);
  const callbacks = useRef({ onSelect, onClose });
  callbacks.current = { onSelect, onClose };
  const t = copy[/^(id|in)/i.test(language) ? "id" : /^zh/i.test(language) ? "zh" : "en"];
  const hasMatch = !!game.matchEntry;
  const hasCreate = !!game.createEntry;

  useEffect(() => {
    selected.current = false;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    if (firstAction.current) firstAction.current.focus();
    else if (panel.current) panel.current.querySelector("button").focus();
    const onKeyDown = event => {
      if (event.key === "Escape") {
        event.preventDefault();
        callbacks.current.onClose();
        return;
      }
      if (event.key !== "Tab" || !panel.current) return;
      const buttons = Array.from(panel.current.querySelectorAll("button:not([disabled])"));
      const first = buttons[0];
      const last = buttons[buttons.length - 1];
      if (!first) return;
      const outside = !panel.current.contains(document.activeElement);
      if (event.shiftKey && (document.activeElement === first || outside)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || outside)) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      if (previousFocus && previousFocus.isConnected && typeof previousFocus.focus === "function") previousFocus.focus();
    };
  }, [game.key, hasMatch, hasCreate]);

  const select = target => {
    if (!target || selected.current) return;
    selected.current = true;
    callbacks.current.onSelect(target);
  };

  return <div class={style.overlay} onClick={event => {
    if (event.target === event.currentTarget) onClose();
  }}>
    <section ref={panel} class={style.panel} role="dialog" aria-modal="true"
      aria-labelledby="mjh5-game-entry-title" aria-describedby="mjh5-game-entry-description">
      <header class={style.header}>
        <h2 id="mjh5-game-entry-title">{game.name}</h2>
        <button class={style.close} type="button" onClick={onClose} aria-label={t.close}>×</button>
      </header>
      <p id="mjh5-game-entry-description" class={style.description}>{hasMatch || hasCreate ? t.subtitle : t.unavailable}</p>
      <div class={style.options} style={hasMatch && hasCreate ? undefined : { gridTemplateColumns: "minmax(0, 1fr)" }}>
        {hasMatch && <button ref={firstAction} type="button" class={`${style.option} ${style.match}`}
          onClick={() => select(game.matchEntry)}>
          <svg class={style.icon} viewBox="0 0 48 48" fill="none" aria-hidden="true" focusable="false">
            <circle cx="18" cy="16" r="7" />
            <path d="M5 39v-4a13 13 0 0 1 26 0v4M31 10a7 7 0 0 1 0 14M36 28a11 11 0 0 1 7 10" />
          </svg>
          <strong>{t.match}</strong>
          <span>{t.matchNote}</span>
        </button>}
        {hasCreate && <button ref={hasMatch ? undefined : firstAction} type="button" class={`${style.option} ${style.create}`} onClick={() => select(game.createEntry)}>
          <svg class={style.icon} viewBox="0 0 48 48" fill="none" aria-hidden="true" focusable="false">
            <path d="m5 21 19-16 19 16M10 19v23h28V19M17 30h14M24 23v14" />
          </svg>
          <strong>{t.create}</strong>
          <span>{t.createNote}</span>
        </button>}
      </div>
    </section>
  </div>;
}
